import * as THREE from 'three';
import React from 'react';
import { S3_BUCKET, CDN } from '@src/components/scene/assets/baseUrl';
import { ASSETS_2022_CDN } from 'util/constants';
import { SceneAsset } from '@src/components/scene/react-three-components/sceneAsset';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { makeId, getCameraRelativePosition } from 'util/helpers';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import { getObjectId, isUndefined } from 'util/helpers';
import { EnvMapType } from '@src/components/generated/graphql';
import { ILight } from '@/components/scene/react-three-components/lightTemplate';

const SCREEN_SHARE_IMAGE =
  'https://d23hwblhugcea5.cloudfront.net/Global/Studio-ui/studioUis/screenShare.jpg';
const DEFAULT_SCENE_TEMPLATE = `${ASSETS_2022_CDN}/studioAssets/artGallery/`;

export const eventSideDrawer = ({
  data,
  camera,
  selectableAssets,
  loadSceneTemplate,
  resetPositionCamera,
  setCurrentCameraConfig,
  setSelectedSkybox,
  setLightTemplate,
  setSelectableAssets,
}) => {
  const { type } = data;
  data.url = data.url?.replace(S3_BUCKET, CDN);
  let assetType;
  let sceneAsset: React.JSX.Element;
  const position = getCameraRelativePosition(camera, 5);
  const rotation = new THREE.Euler(0, 0, 0);
  const instanceId = makeId(10);
  const objectID = getObjectId(data.name, selectableAssets);
  let isEditAsset = false;

  switch (type) {
    case SIDE_MENU_EVENTS.MFE_WORLDS:
    case SIDE_MENU_EVENTS.SCENES:
      loadSceneTemplate(
        data.baseModelURL,
        data?.sceneThumbnail || DEFAULT_SCENE_TEMPLATE
      );
      resetPositionCamera();
      break;

    case SIDE_MENU_EVENTS.GRAPHICS_SETTINGS:
      setCurrentCameraConfig((prevCameraConfig) => {
        const valueOut = data.value;
        return {
          ...prevCameraConfig,
          [data.key]:
            data.key === 'isThirdPerson' ? valueOut : parseFloat(valueOut),
        };
      });
      break;
    case SIDE_MENU_EVENTS.PORTALS:
    case SIDE_MENU_EVENTS.LIBRARY_3D:
    case SIDE_MENU_EVENTS.NFT_ASSETS:
      assetType = {
        type: ASSET_TYPE.GLTF.OTHER,
        isPortal: type === SIDE_MENU_EVENTS.PORTALS,
        marketId:
          type === SIDE_MENU_EVENTS.NFT_ASSETS ? undefined : data?.marketId,
        url: data.url,
        name: `${data.name || data.nameObject}-${objectID}`,
        id: '',
        ref: React.createRef(),
        textureToChange: '',
        textureToChangeNormal: '',
        textureToChangeRoughness: '',
        textureToChangeMetalness: '',
        previewImage: data.thumbnail || '',
        showOnMinimap: false,
      };

      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(1, 1, 1)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );
      isEditAsset = true;
      break;

    case SIDE_MENU_EVENTS.VIDEO_PLANE:
    case SIDE_MENU_EVENTS.IMAGE_PLANE:
      assetType = {
        type:
          type === SIDE_MENU_EVENTS.IMAGE_PLANE
            ? ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE
            : ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE,
        url: data.url,
        name: `${data.name}-${objectID}`,
        previewImage: data.previewImage || data.url || '',
        marketId: data?.marketId,
        shopifyMetadata:
          type === ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE
            ? data?.shopifyMetadata
            : undefined,
        id: '',
        ref: React.createRef(),
      };
      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(5.3, 3, 1)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );
      isEditAsset = true;
      break;
    case SIDE_MENU_EVENTS.SVG_PLANE:
      assetType = {
        type: ASSET_TYPE.PRIMITIVE_TEXTURE.SVG_PLANE,
        url: data.url,
        name: data.name,
        marketId: data?.marketId,
        shopifyMetadata: data?.shopifyMetadata,
        id: '',
        ref: React.createRef(),
      };
      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(5.3, 3, 1)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );
      isEditAsset = true;
      break;

    case SIDE_MENU_EVENTS.AI_AVATARS:
      assetType = {
        type: ASSET_TYPE.AI_AVATARS,
        url: data.url,
        name: `${data.name}-${objectID}`,
        marketId: data?.marketId,
        shopifyMetadata: data?.shopifyMetadata,
        id: '',
        ref: React.createRef(),
        metadata: {
          botConfig: {
            llmConfig: {
              provider: data.config.llmConfig.provider,
              model: data.config.llmConfig.model,
            },
            ttsConfig: {
              provider: data.config.ttsConfig.provider,
              voiceId: data.config.ttsConfig.voiceId,
            },
            botDefinition: data.config.botDefinition,
            botId: data.config.botId,
            avatarType: data.config.avatarType,
          },
        },
      };
      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(1, 1, 1)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );
      isEditAsset = true;
      break;

    case SIDE_MENU_EVENTS.AUDIOS:
      const posAudio = {
        type: ASSET_TYPE.MISC.POSITIONAL_AUDIO,
        url: data.url,
        name: data.name,
        marketId: data?.marketId,
        id: '',
        distance: 10,
        ref: React.createRef(),
      };
      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(3, 3, 3)}
          rotation={rotation}
          instanceId={instanceId}
          asset={posAudio}
        />
      );
      isEditAsset = true;
      break;
    case SIDE_MENU_EVENTS.SCREEN_SHARE:
      assetType = {
        type: ASSET_TYPE.PRIMITIVE_TEXTURE.SCREEN_SHARE,
        url: SCREEN_SHARE_IMAGE,
        name: data.name,
        id: '',
        ref: React.createRef(),
      };
      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(15, 15, 1)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );

      break;

    case SIDE_MENU_EVENTS.YOUTUBE:
    case SIDE_MENU_EVENTS.TWITCH:
    case SIDE_MENU_EVENTS.IFRAME:
      assetType = {
        type: ASSET_TYPE.PRIMITIVE_TEXTURE.STREAM,
        url: SCREEN_SHARE_IMAGE,
        name: data.name,
        id: '',
        ref: React.createRef(),
        metadata: {
          url: data.metadata?.url || '',
          type: data.metadata?.type || 'stream',
        },
      };

      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(0.01, 0.01, 0.001)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );
      isEditAsset = true;
      break;

    ///assets don't edit

    case SIDE_MENU_EVENTS.SKY:
      setSelectedSkybox({
        ...data.object,
        hdriType: data.hdriType || 'day',
        hdriIntensity: data.hdriIntensity || 1.0,
        isSkyBox2025: data.isSkyBox2025 || false,
        envMapType: data.envMapType || EnvMapType.Hdri,
      });
      break;
    case SIDE_MENU_EVENTS.LIGHTS:
      setLightTemplate(data.index);
      break;
    case SIDE_MENU_EVENTS.MML_OBJECTS:
      assetType = {
        type: ASSET_TYPE.MML_OBJECT,
        url: data.url, // This contains the MML code
        name: `${data.name}-${objectID}`,
        id: '',
        ref: data.ref || React.createRef(),
      };

      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={position}
          scale={new THREE.Vector3(1, 1, 1)}
          rotation={rotation}
          instanceId={instanceId}
          asset={assetType}
        />
      );
      isEditAsset = true;
      break;

    case SIDE_MENU_EVENTS.LIGHTSADDED:
      let lightType: ILight = {
        type: data.typeLight,
        ref: React.createRef(),
        position: data.position || position || [0, 0, 0],
        color: data.color || 0xffffff,
        intensity: !isUndefined(data.intensity) ? data.intensity : 1,
      };

      switch (data.typeLight) {
        case 'spot':
          lightType.intensity = !isUndefined(data.intensity)
            ? data.intensity
            : 2;
          lightType.decay = !isUndefined(data.decay) ? data.decay : 0.0;
          lightType.angle = 0.5;
          lightType.penumbra = 0.5;
          break;
        case 'directional':
          lightType.intensity = !isUndefined(data.intensity)
            ? data.intensity
            : 2.5;
          break;
        case 'point':
          lightType.intensity = !isUndefined(data.intensity)
            ? data.intensity
            : 1.5;
          lightType.decay = !isUndefined(data.decay) ? data.decay : 0.0;
          lightType.distance = !isUndefined(data.distance) ? data.distance : 50;
          break;
        case 'hemisphere':
          lightType.groundColor = data.groundColor || 0xffffff;
          lightType.intensity = !isUndefined(data.intensity)
            ? data.intensity
            : 5;
          break;
      }
      assetType = {
        type: ASSET_TYPE.MISC.LIGHT,
        url: '',
        name: `Light-${data.typeLight}-${selectableAssets.length}`,
        id: '',
        ref: React.createRef(),
      };
      sceneAsset = (
        <SceneAsset
          key={`Scene-Asset-${instanceId}`}
          position={data.position || position || [0, 0, 0]}
          scale={new THREE.Vector3(1, 1, 1)}
          rotation={new THREE.Euler(0, 0, 0)}
          instanceId={instanceId}
          asset={assetType}
          light={lightType}
          castShadow={data.castShadow}
        />
      );
      isEditAsset = true;
      break;

    default:
      break;
  }
  if (isEditAsset) {
    setSelectableAssets({ type: 'add', newElement: { ...sceneAsset! } });
  }
};
